import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Option } from '../../../../../../projects/ui/components/dropdown-autocomplete/dropdown-autocomplete.component';
import { FieldTextContentTooltipDirective } from '../../../../../../projects/ui/directives/tooltip/field-text-content-tooltip.directive';
import { ErrorCodeItemViewValue, ServiceTicketStore } from '../../../../../@store/service-ticket';
import { CommonStandaloneModule } from '../../../../common-standalone.module';
import { BannerComponent } from '../../../../components';
import { GTMService } from '../../../../services';

@Component({
  selector: 'app-service-ticket-error-field',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    FormsModule,
    ReactiveFormsModule,
    FieldTextContentTooltipDirective,
    MatCheckboxModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    BannerComponent,
  ],
  templateUrl: './service-ticket-error-field.component.html',
  styleUrls: ['./service-ticket-error-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServiceTicketErrorFieldComponent implements OnInit, OnDestroy {
  @ViewChild('errorCodesSearch') errorCodesSearch: ElementRef<ErrorCodeItemViewValue>;

  @Input() deviceErrors: Option[] = [];
  @Input() detailsInDifferentLanguage = false;

  readonly OTHER_ERROR = this.translateService.instant(
    '+SERVICE_TOOL.SELECT_ERROR_CODES_VALUE_OTHER_ERROR'
  );
  readonly defaultFieldText = this.translateService.instant(
    '+SERVICE_TOOL.SELECT_ERROR_CODES_TITLE'
  );

  noErrorCodeCheckboxValue = false;
  hasOnlyEnterErrorCode: boolean;

  errorCodesForm = new FormControl<string[] | undefined>(undefined);

  fieldText = this.defaultFieldText;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public gtmService: GTMService,
    private serviceTicketStore: ServiceTicketStore,
    private cdr: ChangeDetectorRef,
    protected translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.serviceTicketStore.errorCodes$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((errorCodes) => {
        if (errorCodes.otherErrorSelected) {
          this.errorCodesForm.setValue([this.OTHER_ERROR]);
        }
      });

    if (this.deviceErrors.length === 1) {
      const currentFormData = this.errorCodesForm?.value || [];
      this.errorCodesForm.patchValue([...currentFormData, this.deviceErrors[0].value]);
      this.errorCodeChanged();
    }

    this.hasOnlyEnterErrorCode =
      this.deviceErrors[0]?.value === this.translateService.instant('+SERVICE_TOOL.ERROR_CODE');

    const errorOption: Option = {
      value: this.OTHER_ERROR,
      viewValue: this.OTHER_ERROR,
    };

    const isDeviceErrorsHaveOtherOption = this.deviceErrors.some(
      (error) => error.value === this.OTHER_ERROR
    );

    if (!this.hasOnlyEnterErrorCode && !isDeviceErrorsHaveOtherOption) {
      this.deviceErrors.push(errorOption);
    }

    this.cdr.detectChanges();
  }

  errorCodeChanged(): void {
    this.updateErrorCodesFormText(this.errorCodesForm.value);
    const otherErrorSelected = this.errorCodesForm.value.some((entry) =>
      entry.includes(this.OTHER_ERROR)
    );
    const codesWithoutOtherError = this.errorCodesForm.value.filter(
      (entry) => !entry.includes(this.OTHER_ERROR)
    );

    this.serviceTicketStore.updateSelectedErrorCodes(codesWithoutOtherError, otherErrorSelected);
    this.cdr.markForCheck();
  }

  onNoErrorCodeCheckboxChange(checkboxValue: boolean): void {
    this.noErrorCodeCheckboxValue = checkboxValue;
    this.serviceTicketStore.updateNoErrorCodeSelection(checkboxValue);
  }

  private updateErrorCodesFormText(errorCodes: string[]): void {
    const errorsToDisplay = [];
    let _fieldText = '';

    errorCodes.slice(0, 3).forEach((entry) => {
      errorsToDisplay.push(entry);
    });

    _fieldText = errorsToDisplay.join(', ');

    if (errorCodes.length > 3) {
      _fieldText += ` +${errorCodes.length - 3} ${this.translateService.instant(
        '+SERVICE_TOOL.SELECT_ERROR_CODES_VALUE_OTHER_SUFFIX'
      )}`;
    }

    this.fieldText = _fieldText;
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
